import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Printer, Trophy, ShoppingBag, Package, IndianRupee, Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, errMsg } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";

const toISO = (d) => d.toISOString().slice(0, 10);

const presetRange = (key) => {
  const today = new Date();
  if (key === "today") { const s = toISO(today); return [s, s]; }
  if (key === "yesterday") { const y = new Date(today); y.setDate(y.getDate() - 1); const s = toISO(y); return [s, s]; }
  if (key === "week") { const m = new Date(today); m.setDate(m.getDate() - ((m.getDay() + 6) % 7)); return [toISO(m), toISO(today)]; }
  return [null, null];
};

const AdminDispatch = () => {
  const navigate = useNavigate();
  const [preset, setPreset] = useState("today");
  const [custom, setCustom] = useState(toISO(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [start, end] = preset === "custom" ? [custom, custom] : presetRange(preset);

  const load = () => {
    if (!start) return;
    setLoading(true);
    api.get("/admin/daily-summary", { params: { start, end } })
      .then((r) => { setData(r.data); setLoading(false); })
      .catch((e) => { toast.error(errMsg(e)); setLoading(false); });
  };
  useEffect(load, [start, end]);

  const stats = data ? [
    { id: "orders", icon: ShoppingBag, label: "Confirmed Orders", value: data.total_orders },
    { id: "items", icon: Package, label: "Items Sold", value: data.total_items },
    { id: "sales", icon: IndianRupee, label: "Total Sales", value: formatINR(data.total_sales) },
    { id: "revenue", icon: Wallet, label: "Daily Revenue", value: formatINR(data.daily_revenue) },
    { id: "best", icon: Trophy, label: "Best Seller", value: data.best_seller || "—" },
  ] : [];

  const dateLabel = start === end ? start : `${start} → ${end}`;

  return (
    <AdminLayout title="Daily Orders & Dispatch">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["custom", "Custom Date"]].map(([k, label]) => (
          <button key={k} data-testid={`dispatch-${k}`} onClick={() => setPreset(k)}
            className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors min-h-[44px] ${preset === k ? "bg-choco text-cream" : "bg-white border border-[#E5DFD3] text-chocodark hover:border-goldsoft"}`}>
            {label}
          </button>
        ))}
        {preset === "custom" && (
          <input data-testid="dispatch-custom-date" type="date" value={custom} onChange={(e) => setCustom(e.target.value)}
            className="h-11 rounded-full bg-white border border-[#E5DFD3] px-4 text-sm text-choco outline-none focus:ring-2 focus:ring-gold/40" />
        )}
        <div className="flex-1" />
        <button data-testid="print-addresses-btn" onClick={() => navigate(`/admin/dispatch/print?start=${start}&end=${end}`)}
          className="inline-flex items-center gap-2 rounded-xl bg-choco text-cream font-semibold px-5 py-3 hover:bg-chocodark transition-colors min-h-[48px]">
          <Printer size={18} /> Print Addresses
        </button>
      </div>

      <p className="text-sm text-muted-foreground mb-4">Showing confirmed (paid) orders for <span data-testid="dispatch-date-label" className="font-medium text-choco">{dateLabel}</span></p>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 size={36} className="animate-spin text-goldsoft" /></div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
            {stats.map((s) => (
              <div key={s.id} data-testid={`stat-${s.id}`} className="bg-white rounded-2xl border border-[#E5DFD3] p-5">
                <div className="h-10 w-10 rounded-full bg-mutedcream flex items-center justify-center text-goldsoft mb-3"><s.icon size={20} /></div>
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <p className="font-display text-xl font-bold text-choco mt-1 line-clamp-2">{s.value}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-[#E5DFD3] overflow-hidden">
            <p className="font-display text-lg font-semibold text-choco p-5 pb-3">Product-wise Quantity Sold</p>
            {data.product_quantities.length === 0 ? (
              <p data-testid="dispatch-empty" className="text-sm text-muted-foreground px-5 pb-6">No confirmed orders for this date.</p>
            ) : (
              <div className="divide-y divide-[#EFEBE1]">
                {data.product_quantities.map((p) => (
                  <div key={p.name} data-testid={`dispatch-product-${p.name}`} className="flex justify-between items-center px-5 py-3.5">
                    <span className="text-sm text-choco">{p.name}</span>
                    <span className="text-sm font-bold text-choco bg-cream rounded-full px-3 py-1">× {p.quantity}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDispatch;
